import { Link } from "react-router-dom";
import { useState } from "react";
import { Mail, Phone, MapPin, Linkedin, Youtube, Twitter } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed. Welcome to the GMS insight list.");
      setEmail("");
    } catch { toast.error("Could not subscribe. Try again."); }
    finally { setBusy(false); }
  };

  return (
    <footer className="bg-[#0F3D66] text-white mt-24" data-testid="footer">
      <div className="gms-container py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white flex items-center justify-center">
              <span className="text-[#0F3D66] font-heading font-extrabold text-lg leading-none">G</span>
            </div>
            <div>
              <div className="font-heading font-extrabold text-lg tracking-tight">GMS</div>
              <div className="text-[10px] uppercase tracking-widest opacity-80">Graphic Machinery Solutions</div>
            </div>
          </div>
          <p className="text-sm text-white/70 leading-relaxed mb-6 max-w-sm">
            India's premium supplier of printing & packaging machinery, industrial automation and engineering services. Trusted by manufacturers who value precision.
          </p>
          <form onSubmit={subscribe} className="flex gap-2" data-testid="footer-newsletter">
            <input value={email} onChange={(e)=>setEmail(e.target.value)} type="email" required placeholder="Your business email" className="flex-1 bg-white/10 border border-white/20 px-3 py-2 text-sm rounded-sm placeholder:text-white/50 focus:outline-none focus:border-[#007BFF]" data-testid="footer-newsletter-input" />
            <button disabled={busy} className="bg-[#007BFF] hover:bg-[#0056b3] px-4 py-2 text-sm font-semibold rounded-sm" data-testid="footer-newsletter-submit">{busy?"…":"Subscribe"}</button>
          </form>
        </div>

        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-widest text-white/60 font-bold mb-4">Company</div>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/about" className="hover:text-white">About Us</Link></li>
            <li><Link to="/solutions" className="hover:text-white">Solutions</Link></li>
            <li><Link to="/services" className="hover:text-white">Services</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-widest text-white/60 font-bold mb-4">Solutions</div>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/products" className="hover:text-white">All Products</Link></li>
            <li><Link to="/machine-finder" className="hover:text-white">Machine Finder</Link></li>
            <li><Link to="/industries" className="hover:text-white">Industries</Link></li>
            <li><Link to="/services" className="hover:text-white">Engineering Services</Link></li>
            <li><Link to="/faq" className="hover:text-white">FAQs</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-4">
          <div className="text-xs uppercase tracking-widest text-white/60 font-bold mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75}/> <span>A 9, Ground Floor, Pocket A, Vikas Puri Extension, Outer Ring Road, New Delhi 110018, India</span></li>
            <li className="flex items-center gap-3"><Phone className="w-4 h-4" strokeWidth={1.75}/> <a href="tel:+919810012256" className="hover:text-white">+91 98100 12256</a></li>
            <li className="flex items-center gap-3"><Mail className="w-4 h-4" strokeWidth={1.75}/> <a href="mailto:info@graphicmachinerysolutions.com" className="hover:text-white">info@graphicmachinerysolutions.com</a></li>
          </ul>
          <div className="flex gap-3 mt-5">
            <a href="https://www.linkedin.com/" target="_blank" rel="noopener noreferrer nofollow" aria-label="LinkedIn" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-white transition-colors"><Linkedin className="w-4 h-4" strokeWidth={1.75}/></a>
            <a href="https://www.youtube.com/" target="_blank" rel="noopener noreferrer nofollow" aria-label="YouTube" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-white transition-colors"><Youtube className="w-4 h-4" strokeWidth={1.75}/></a>
            <a href="https://twitter.com/" target="_blank" rel="noopener noreferrer nofollow" aria-label="Twitter" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-white transition-colors"><Twitter className="w-4 h-4" strokeWidth={1.75}/></a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="gms-container py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/60">
          <div>© {new Date().getFullYear()} Graphic Machinery Solutions Pvt Ltd. All rights reserved.</div>
          <div className="flex gap-5">
            <Link to="/faq" className="hover:text-white">FAQ</Link>
            <Link to="/admin/login" className="hover:text-white" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
